import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert
import "./Forms.css";
import "./RecordPayementForm.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ClipLoader } from "react-spinners";
import Select from "react-select"; // Import react-select
import axiosInstance from "../axiosinstance";

export default function RecordPaymentForm({ isSidebarExpanded }) {
  const [payment_mode, setPaymentMode] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState("");
  const [payment_date, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [amount, setAmount] = useState("");
  const [chequeDetails, setChequeDetails] = useState({
    cheque_numbers: ["", "", "", "", "", ""], // Initialize as an array of 6 empty strings
    cheque_date: "",
    bank_name: "",
  });
  const inputRefs = useRef([]);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error"); // error by default
  const [transaction_id, setTransactionId] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const clientDropdownRef = useRef(null);
  const navigate = useNavigate();
  const handlePaymentModeChange = (e) => {
    const { value } = e.target;

    setPaymentMode(value); // Update the state
    handleInputChange("payment_mode", value); // Clear error when user selects
  };

  const handleClientChange = (selectedOption) => {
    if (!selectedOption) return;
    setSelectedClient({
      client_id: selectedOption.value, // assuming `value` stores client_id
      client_name: selectedOption.label, // assuming `label` stores client_name
    });
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;

    // Prevent negative values
    if (value < 0) return;

    setAmount(value);

    // Clear error when user starts typing
    if (errors.amount) {
      setErrors((prevErrors) => ({ ...prevErrors, amount: "" }));
    }
  };

  const handleChequeDetailChange = (event) => {
    const { name, value } = event.target;

    setChequeDetails({
      ...chequeDetails, // Preserve other values
      [name]: value, // Update the specific field
    });

    // Remove error when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined, // Clears the error for this specific field
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChequeDetaildate = (date) => {
    setChequeDetails({
      ...chequeDetails,
      cheque_date: date,
    });
  };
  const handleTransactionIdChange = (e) => {
    setTransactionId(e.target.value);
  };
  const handlePaymentDateChange = (date) => {
    const formattedDate = date ? date.toISOString().split("T")[0] : "";
    setPaymentDate(formattedDate);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axiosInstance.get(`/api/get_client_name`);
        setClients(response.data.data);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch clients");
        setLoading(false);
      }
    };
    fetchClients();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target)
      ) {
        setIsClientDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const validateForm = () => {
    const validationErrors = {};

    // Client Validation
    if (!selectedClient || selectedClient.toString().trim() === "") {
      validationErrors.selectedClient = "Client is required.";
    }

    // Payment Date Validation
    if (!payment_date) {
      validationErrors.payment_date = "Payment date is required.";
    }

    // Payment Mode Validation
    if (!payment_mode || payment_mode.toString().trim() === "") {
      validationErrors.payment_mode = "Payment mode is required.";
    }

    // Amount Validation
    if (!amount || amount.toString().trim() === "") {
      validationErrors.amount = "Amount is required.";
    } else if (isNaN(amount) || Number(amount) <= 0) {
      validationErrors.amount = "Amount must be a positive number.";
    }

    // Cheque Payment Validation
    if (payment_mode === "cheque") {
      if (
        !chequeDetails.cheque_numbers ||
        chequeDetails.cheque_numbers.length === 0 ||
        chequeDetails.cheque_numbers.every((num) => num.trim() === "")
      ) {
        validationErrors.cheque_numbers = "Cheque number is required.";
      } else if (
        !chequeDetails.cheque_numbers.every((num) => /^\d+$/.test(num))
      ) {
        validationErrors.cheque_numbers = "Cheque number must be numeric.";
      }

      if (!chequeDetails.bank_name || chequeDetails.bank_name.trim() === "") {
        validationErrors.bank_name = "Bank name is required.";
      }

      if (!chequeDetails.cheque_date) {
        validationErrors.cheque_date = "Cheque date is required.";
      }
    }

    // Online Payment Validation
    if (payment_mode === "online") {
      if (!transaction_id || transaction_id.trim() === "") {
        validationErrors.transaction_id =
          "Transaction ID is required for online payment.";
      }
    }

    return validationErrors;
  };

  const handleInputChange = (field, value) => {
    if (errors[field]) {
      setErrors((prevErrors) => ({ ...prevErrors, [field]: null })); // Remove error when user starts typing
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true); // Start loading state
      const concatenatedChequeNumbers = chequeDetails.cheque_numbers.join("");

      const paymentData = {
        client_id: selectedClient.client_id,
        payment_date: new Date(payment_date).toISOString().split("T")[0],
        payment_mode,
        amount,
        cheque_number:
          payment_mode === "cheque" ? concatenatedChequeNumbers : "",
        cheque_date:
          payment_mode === "cheque"
            ? new Date(chequeDetails.cheque_date).toISOString().split("T")[0]
            : null,
        bank_name: payment_mode === "cheque" ? chequeDetails.bank_name : null,
        transaction_id: payment_mode === "online" ? transaction_id : null,
      };

      const response = await axiosInstance.post(
        `/api/create_payment`,
        paymentData
      );
      if (response.status === 200) {
        setSnackbarSeverity("success");
        setSnackbarMessage("Record Payment added successfully!");
        setSnackbarOpen(true);

        // Disable form inputs
        const form = e.target.closest("form");
        if (form) {
          const inputs = form.querySelectorAll("input, select, button");
          inputs.forEach((input) => (input.disabled = true));
        }

        // Navigate after delay
        setTimeout(() => {
          navigate("/record-payement");
        }, 2000);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarSeverity("error");
      // Use the backend error message if available, otherwise fallback to default message
      const errorMessage = error.response?.data?.message || "Failed to add payment. Please try again.";
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
      setIsSubmitting(false); // Only reset on error
    }
  };

  const handlecancel = () => {
    if (!isSubmitting) {
      window.history.back();
    }
  };

  useEffect(() => {
    return () => {
      setIsSubmitting(false);
    };
  }, []);

  const handleChequeChange = (e, index) => {
    const rawValue = (e.target.value || "").replace(/\D/g, "");
    const maxLength = chequeDetails.cheque_numbers.length;

    if (!rawValue) {
      setChequeDetails((prev) => {
        const updatedChequeNumbers = [...prev.cheque_numbers];
        updatedChequeNumbers[index] = "";
        return { ...prev, cheque_numbers: updatedChequeNumbers };
      });
      return;
    }

    setChequeDetails((prev) => {
      const updatedChequeNumbers = [...prev.cheque_numbers];

      if (rawValue.length === 1) {
        updatedChequeNumbers[index] = rawValue;
      } else {
        for (
          let i = index, j = 0;
          i < maxLength && j < rawValue.length;
          i += 1, j += 1
        ) {
          updatedChequeNumbers[i] = rawValue[j];
        }
      }

      return { ...prev, cheque_numbers: updatedChequeNumbers };
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      cheque_numbers: undefined,
    }));

    const nextIndex =
      rawValue.length > 1
        ? Math.min(index + rawValue.length, maxLength - 1)
        : Math.min(index + 1, maxLength - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleChequeKeyDown = (e, index) => {
    const maxLength = chequeDetails.cheque_numbers.length;
    const currentValue = chequeDetails.cheque_numbers[index] || "";

    if (e.key === "Backspace") {
      e.preventDefault();

      setChequeDetails((prev) => {
        const updatedChequeNumbers = [...prev.cheque_numbers];

        if (updatedChequeNumbers[index]) {
          updatedChequeNumbers[index] = "";
          return { ...prev, cheque_numbers: updatedChequeNumbers };
        }

        if (index > 0) {
          updatedChequeNumbers[index - 1] = "";
        }

        return { ...prev, cheque_numbers: updatedChequeNumbers };
      });

      if (!currentValue && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        cheque_numbers: undefined,
      }));
      return;
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (index > 0) inputRefs.current[index - 1]?.focus();
      return;
    }

    if (e.key === "ArrowRight") {
      e.preventDefault();
      if (index < maxLength - 1) inputRefs.current[index + 1]?.focus();
      return;
    }
  };

  const handleChequePaste = (e, index) => {
    e.preventDefault();
    const pastedValue = (e.clipboardData.getData("text") || "").replace(
      /\D/g,
      ""
    );
    if (!pastedValue) return;

    const maxLength = chequeDetails.cheque_numbers.length;
    let lastFilledIndex = index;

    setChequeDetails((prev) => {
      const updatedChequeNumbers = [...prev.cheque_numbers];

      for (
        let i = index, j = 0;
        i < maxLength && j < pastedValue.length;
        i += 1, j += 1
      ) {
        updatedChequeNumbers[i] = pastedValue[j];
        lastFilledIndex = i;
      }

      return { ...prev, cheque_numbers: updatedChequeNumbers };
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      cheque_numbers: undefined,
    }));

    inputRefs.current[lastFilledIndex]?.focus();
  };

  return (
    <div
      className={`table-container record-payment-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body record-payment-form-content">
        <div className="record-payment-page-heading">
          <h1 className="form-title jost-bold">New Record Payment</h1>
          <p className="record-payment-page-subtitle">
            Record a payment received from a client.
          </p>
        </div>
        <div className="record-payment-form-card">
          <div className="form-body record-payment-form-body">
          <form className="form-box record-payment-form-box">
            <div className="form-body-container record-payment-form-sections">
              <section className="record-payment-section">
                <div className="record-payment-section-header">
                  <div>
                    <h2 className="record-payment-section-title">
                      Payment Information
                    </h2>
                    <p className="record-payment-section-description">
                      Select the client, payment date and received amount.
                    </p>
                  </div>
                </div>
              <div className="form-group record-payment-span-2">
                <label className="form-label">
                  Client
                  <span className="required">*</span>
                </label>
                {loading ? (
                  <p>Loading clients...</p>
                ) : error ? (
                  <p>{error}</p>
                ) : (
                  <>
                    <Select
                      value={
                        selectedClient
                          ? {
                            label: selectedClient.client_name,
                            value: selectedClient.client_id,
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        if (!selectedOption) return;
                        setSelectedClient({
                          client_id: selectedOption.value,
                          client_name: selectedOption.label,
                        });
                        handleInputChange("selectedClient", selectedOption);
                      }}
                      options={clients.map((client) => ({
                        label: client.client_name,
                        value: client.client_id,
                      }))}
                      className={`select-field-invoice ${errors.selectedClient ? "input-error" : ""
                        }`}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 999999,
                          position: "absolute",
                        }),
                        container: (base) => ({
                          ...base,
                          zIndex: 999999,
                          position: "relative",
                        }),
                      }}
                      menuPortalTarget={document.body}
                      placeholder="Select Client"
                    />

                    {errors.selectedClient && (
                      <p className="error-message">{errors.selectedClient}</p>
                    )}
                  </>
                )}
              </div>

              <div className="form-group unique-form-group">
                <label className="form-label jost-regular">
                  Payment Date <span className="required">*</span>
                </label>
                <div className="form-inputs form-inputs-payment custom-input-wrapper">
                  <DatePicker
                    selected={    
                      payment_date ? new Date(payment_date) : new Date()
                    } // Default to today
                    onChange={(date) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      setPaymentDate(formattedDate);
                      handleInputChange("payment_date", formattedDate);
                    }}
                    className="input-field input-field-paymentdate custom-datepicker"
                    dateFormat="dd-MM-yyyy"
                    required
                    wrapperClassName="custom-datepicker-wrapper"
                    calendarClassName="custom-datepicker-calendar"
                    placeholderText="Enter Payment Date"
                    showYearDropdown
                    showMonthDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    open={false} // 👈 Prevents calendar popup
                  />

                  {errors.payment_date && (
                    <p className="error-message">{errors.payment_date}</p>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label jost-regular">
                  Amount <span className="required">*</span>
                </label>
                <div className="amount-input">
                  <input
                    type="number"
                    className={`input-fields-amount ${
                      errors.amount ? "input-error" : ""
                    }`}
                    value={amount}
                    onChange={handleAmountChange}
                    required
                    min="0"
                    placeholder="Enter Amount"
                  />
                </div>
                {errors.amount && (
                  <p className="error-message">{errors.amount}</p>
                )}
              </div>
              </section>

              <section className="record-payment-section record-payment-section-last">
                <div className="record-payment-section-header">
                  <div>
                    <h2 className="record-payment-section-title">
                      Payment Mode
                    </h2>
                    <p className="record-payment-section-description">
                      Choose how the payment was received and complete any
                      related details.
                    </p>
                  </div>
                </div>

              <div className="form-group record-payment-span-2">
                <label className="form-label jost-regular">
                  Payment Type <span className="required">*</span>
                </label>
                <div className="payment-radio">
                  <div className="form-inputs">
                    <div className="payment-type">
                      <label
                        className={`radio-label-record record-payment-option ${
                          payment_mode === "online" ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMode"
                          value="online"
                          checked={payment_mode === "online"}
                          onChange={handlePaymentModeChange}
                          required
                          className="record-radio-payment"
                        />
                        <span className="radio-text">Online</span>
                      </label>

                      <label
                        className={`radio-label-record record-payment-option ${
                          payment_mode === "cash" ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMode"
                          value="cash"
                          checked={payment_mode === "cash"}
                          onChange={handlePaymentModeChange}
                          required
                          className="record-radio-payment"
                        />
                        <span className="radio-text">Cash</span>
                      </label>

                      <label
                        className={`radio-label-record record-payment-option ${
                          payment_mode === "cheque" ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMode"
                          value="cheque"
                          checked={payment_mode === "cheque"}
                          onChange={handlePaymentModeChange}
                          required
                          className="record-radio-payment"
                        />
                        <span className="radio-text">Cheque</span>
                      </label>
                    </div>
                  </div>
                </div>
                {errors.payment_mode && (
                  <p className="error-message">{errors.payment_mode}</p>
                )}
              </div>
              <div className="payment-condition record-payment-span-2">
                {payment_mode === "cheque" && (
                  <>
                    <div className="check-box">
                      <div className="form-group">
                        <label className="form-labelss jost-regular"></label>
                      </div>
                      <div className="payment-section">
                        {/* Cheque Number Input */}
                        <div className="form-group-1 cheque-payment">
                          <label className="form-labels jost-regular">
                            Cheque No <span className="required">*</span>
                          </label>
                          <div className="form-inputs-chequeNO">
                            {chequeDetails.cheque_numbers.map(
                              (number, index) => (
                                <input
                                  key={index}
                                  type="text"
                                  className={`input-field-record ${errors.cheque_numbers ? "input-error" : ""
                                    }`}
                                  value={number}
                                  onChange={(e) => handleChequeChange(e, index)}
                                  onKeyDown={(e) =>
                                    handleChequeKeyDown(e, index)
                                  }
                                  onPaste={(e) => handleChequePaste(e, index)}
                                  onFocus={(e) => e.target.select()}
                                  maxLength={1}
                                  inputMode="numeric"
                                  autoComplete="off"
                                  required
                                  ref={(el) => (inputRefs.current[index] = el)} // Assign ref to each input
                                />
                              )
                            )}
                          </div>
                          {errors.cheque_numbers && (
                            <p className="error-message">
                              {errors.cheque_numbers}
                            </p>
                          )}
                        </div>

                        {/* Bank Name Input */}
                        <div className="form-group">
                          <input
                            type="text"
                            className={`input-field payment-bank-name ${errors.bank_name ? "input-error" : ""
                              }`}
                            name="bank_name"
                            value={chequeDetails.bank_name}
                            onChange={handleChequeDetailChange}
                            required
                            placeholder="Enter bank name"
                          />
                          {errors.bank_name && (
                            <p className="error-message">{errors.bank_name}</p>
                          )}
                        </div>

                        {/* Cheque Date Picker */}
                        <div className="form-group-1">
                          <label className="form-labels jost-regular"></label>
                          <div className="form-inputs">
                            <DatePicker
                              selected={chequeDetails.cheque_date}
                              onChange={(date) => {
                                handleChequeDetaildate(date);
                                handleInputChange("cheque_date", date);
                              }}
                              dateFormat="yyyy-MM-dd"
                              className={`input-field record-payement-date ${errors.cheque_date ? "input-error" : ""
                                }`}
                              wrapperClassName="custom-datepicker-wrapper"
                              calendarClassName="custom-datepicker-calendar"
                              placeholderText="Select Cheque Date"
                              required
                              showYearDropdown
                              showMonthDropdown
                              yearDropdownItemNumber={100} // Number of years to show in dropdown
                              scrollableYearDropdown // Allows scrolling
                              popperPlacement="bottom-start"
                              popperClassName="record-payment-datepicker-popper"
                            />
                            {errors.cheque_date && (
                              <p className="error-message">
                                {errors.cheque_date}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {payment_mode === "online" && (
                  <div className="form-group">
                    <label className="form-label"></label>
                    <input
                      type="text"
                      className={`input-field bankName ${errors.transaction_id ? "input-error" : ""
                        }`}
                      value={transaction_id}
                      onChange={(e) => {
                        handleTransactionIdChange(e);
                        handleInputChange("transaction_id", e.target.value);
                      }}
                      required
                      placeholder="Enter Your Transaction ID"
                    />
                    {errors.transaction_id && (
                      <p className="error-message">{errors.transaction_id}</p>
                    )}
                  </div>
                )}
              </div>

              </section>
            </div>
          </form>
          {error && <p className="error-message">{error}</p>}
        </div>

      <div className="form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handlecancel}
            disabled={isSubmitting}
          >
            CANCEL
          </button>
          <button
            type="submit"
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              opacity: isSubmitting ? 0.7 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting ? (
              <span className="button-content">
                <ClipLoader size={16} color="#FFF" />
                <span style={{ marginLeft: "8px" }}>
                  {snackbarSeverity === "success"
                    ? "Payment Recorded!"
                    : "Recording Payment..."}
                </span>
              </span>
            ) : (
              "SAVE"
            )}
          </button>
        </div>
      </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(50px)", marginLeft: "20px" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}  // Changed from "success" to use the state variable
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      </div>
    </div>
  );
}
